import React, { useEffect, useRef } from 'react';
import { Button } from '../v2/ui/components/button';
import { IMG_PATH } from '../v2/constants';
import {
  PROCESSING_COPY,
  STATUS_COPY,
  TIMEOUT_COPY,
  undeliverableEmailWithFieldBody,
} from '../data/application-copy';

// Neutral outcomes are not failures and must not read as errors.
const STATUS_TONE = {
  SUCCEEDED: 'success',
  DUPLICATE: 'neutral',
  POSITION_CLOSED: 'neutral',
  UNREADABLE_CV: 'error',
  FAILED: 'error',
  UNDELIVERABLE_EMAIL: 'error',
};

const TONE_MARK = { success: '✓', neutral: 'i', error: '!' };

const resultCopy = (outcome, emailFieldName) => {
  if (outcome.stage === 'processing') return PROCESSING_COPY;
  if (outcome.stage === 'timeout') return TIMEOUT_COPY;
  if (outcome.status === 'UNDELIVERABLE_EMAIL' && emailFieldName) {
    return {
      title: STATUS_COPY.UNDELIVERABLE_EMAIL.title,
      body: undeliverableEmailWithFieldBody(emailFieldName),
    };
  }
  return STATUS_COPY[outcome.status];
};

// emailFieldName is present when the position asks for an email. retry is
// { label, onClick } for UNDELIVERABLE_EMAIL only.
const ApplicationResult = ({ outcome, emailFieldName, retry }) => {
  const cardRef = useRef(null);

  // Focus moves here once, when the form is replaced; later stage changes are
  // announced through the live region instead of stealing focus again.
  useEffect(() => {
    if (!cardRef.current) return;
    cardRef.current.focus({ preventScroll: true });
    cardRef.current.scrollIntoView({ block: 'center' });
  }, []);

  const tone =
    outcome.stage === 'processing'
      ? 'progress'
      : outcome.stage === 'timeout'
        ? 'neutral'
        : STATUS_TONE[outcome.status];
  const { title, body } = resultCopy(outcome, emailFieldName);

  return (
    <div role="status" aria-live="polite">
      <div
        ref={cardRef}
        tabIndex={-1}
        className={`__application-result __application-result-${tone}`}
      >
        <span className="__application-result-mark" aria-hidden="true">
          {tone === 'progress' ? (
            <img src={`${IMG_PATH}/loading.svg`} alt="" />
          ) : (
            TONE_MARK[tone]
          )}
        </span>
        <div className="__application-result-text">
          <h4>{title}</h4>
          <p>{body}</p>
          {retry && (
            <Button variant="orange" onClick={retry.onClick}>
              {retry.label}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApplicationResult;
